import { Injectable } from '@angular/core';
import { OauthAEMConfig, TokenService } from '@com-delta-idp/mnx-oauth';
import { LogoutService } from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { AppConfigService } from '@com-delta-idp/mnx-utils';
import { take } from 'rxjs/operators';

import {
  getQueryParameter,
  trackErrorInTrackJs
} from '../adapters/utils/utils.adapter';
import { QUERY_PARAMS } from '../constants/common.constants';
import { BOOK_WIDGET_ERROR_CODES } from '../constants/error-codes.constants';
import { EndpointKeys, IDP_APPS } from '../constants/urls.constants';
import { AppConfig } from '../models/app-config.interface';

@Injectable({ providedIn: 'root' })
export class UserAuthServices {
  private cachedToken: string | null = null;

  constructor(
    private tokenService: TokenService,
    private logoutService: LogoutService,
    private appConfigService: AppConfigService
  ) {}

  fetchAccessToken = async (): Promise<string | null> => {
    if (this.cachedToken) return this.cachedToken;
    const authConfig: OauthAEMConfig = await this._getAuthConfig();
    try {
      const { access_token = '', expires_in = 0 } =
        await this.fetchAccessTokenDetails(authConfig);
      if (!access_token) {
        trackErrorInTrackJs(
          BOOK_WIDGET_ERROR_CODES.NO_AUTH_TOKEN_BUT_USER_LOGGED_IN,
          getQueryParameter(QUERY_PARAMS.CACHE_KEY_SUFFIX)
        );
        this.logoutService.logOut(window.location.href, true);
      }
      this.cachedToken = access_token;
      this.setTokenExpiry(expires_in);
      return access_token;
    } catch (error) {
      trackErrorInTrackJs(
        BOOK_WIDGET_ERROR_CODES.NO_AUTH_TOKEN_BUT_USER_LOGGED_IN,
        getQueryParameter(QUERY_PARAMS.CACHE_KEY_SUFFIX)
      );
      this.logoutService.logOut(window.location.href, true);
      return null;
    }
  };

  private async fetchAccessTokenDetails(authConfig: OauthAEMConfig) {
    return await this.tokenService.getAccessToken(authConfig);
  }

  private setTokenExpiry(expiryTime: number) {
    setTimeout(() => {
      if (this.cachedToken) {
        trackErrorInTrackJs(
          BOOK_WIDGET_ERROR_CODES.MAKING_TOKEN_NULL_AFTER_30_MINS,
          getQueryParameter(QUERY_PARAMS.CACHE_KEY_SUFFIX)
        );
      }
      this.cachedToken = null;
    }, expiryTime * 1000);
  }

  private async _getAuthConfig(): Promise<OauthAEMConfig> {
    const config = await this.appConfigService
      .getConfigValue(IDP_APPS.BOOK_WIDGET, EndpointKeys.APP_CONFIGURATIONS)
      .pipe(take(1))
      .toPromise();
    return (config as unknown as AppConfig).authConfig;
  }
}
